import { useState, useEffect, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router";
import { Badge, Avatar, Skeleton, Select, EmptyState } from "@/components/ds";
import { usePageTitle } from "../hooks/usePageTitle";
import type { VisitStatus } from "@/domain/visits/types";
import styles from "./VisitsPage.module.css";

// ── Types ──

interface Visit {
  readonly shiftId: string;
  readonly date: string;
  readonly siteId: string;
  readonly siteName: string;
  readonly membershipId: string;
  readonly cleanerName: string;
  readonly scheduledStart: string;
  readonly scheduledEnd: string;
  readonly checkInTime: string | null;
  readonly checkOutTime: string | null;
  readonly durationMinutes: number | null;
  readonly status: VisitStatus;
  readonly checkInVerification: string | null;
}

interface VisitSummary {
  readonly totalVisits: number;
  readonly completed: number;
  readonly onTimePercent: number | null;
  readonly issues: number;
  readonly totalTrackedMinutes: number;
}

interface FilterOption {
  readonly value: string;
  readonly label: string;
}

interface VisitsData {
  readonly visits: Visit[];
  readonly summary: VisitSummary;
  readonly sites: FilterOption[];
  readonly cleaners: FilterOption[];
  readonly dateRange: { readonly from: string; readonly to: string };
}

// ── Date helpers ──

function yesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

function getMondayOfWeek(ref: Date): string {
  const d = new Date(ref);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

function getSundayOfWeek(ref: Date): string {
  const d = new Date(ref);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? 0 : 7);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

interface DatePreset {
  readonly label: string;
  readonly from: string;
  readonly to: string;
}

function getPresets(): DatePreset[] {
  const y = yesterday();
  const now = new Date();

  const thisMonday = getMondayOfWeek(now);

  const lastWeekRef = new Date();
  lastWeekRef.setDate(lastWeekRef.getDate() - 7);
  const lastMonday = getMondayOfWeek(lastWeekRef);
  const lastSunday = getSundayOfWeek(lastWeekRef);

  return [
    { label: "Yesterday", from: y, to: y },
    { label: "This week", from: thisMonday, to: y },
    { label: "Last week", from: lastMonday, to: lastSunday },
    { label: "Last 30 days", from: daysAgo(30), to: y },
  ];
}

function activePresetIndex(from: string, to: string, presets: DatePreset[]): number {
  return presets.findIndex((p) => p.from === from && p.to === to);
}

// ── Format helpers ──

function formatTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${period}` : `${h12}:${mStr}${period}`;
}

function formatTimeRange(start: string, end: string): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

function formatCheckTime(isoTime: string): string {
  const d = new Date(isoTime);
  const h = d.getUTCHours();
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  const period = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === "00" ? `${h12}${period}` : `${h12}:${m}${period}`;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatDateHeader(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

// ── Status config ──

const visitStatusBadge: Record<VisitStatus, { label: string; variant: "danger" | "warning" | "success" | "neutral" }> = {
  no_show: { label: "No show", variant: "danger" },
  short: { label: "Short", variant: "warning" },
  late: { label: "Late", variant: "warning" },
  no_checkout: { label: "No check-out", variant: "neutral" },
  completed: { label: "Done", variant: "success" },
};

const visitStatusStrip: Record<VisitStatus, string> = {
  no_show: styles.stripDanger,
  short: styles.stripWarning,
  late: styles.stripWarning,
  no_checkout: styles.stripNeutral,
  completed: styles.stripSuccess,
};

const gpsBadgeLabels: Record<string, string> = {
  manual_override: "Manual override",
  gps_unavailable: "GPS unavailable",
  gps_timeout: "GPS timeout",
  auto_closed: "Auto closed",
};

const COL_COUNT = 7;

// ── Group visits by date ──

function groupByDate(visits: readonly Visit[]): Map<string, Visit[]> {
  const groups = new Map<string, Visit[]>();
  for (const visit of visits) {
    const list = groups.get(visit.date) ?? [];
    list.push(visit);
    groups.set(visit.date, list);
  }
  return groups;
}

// ── Components ──

function StatusCell({ status }: { readonly status: VisitStatus }) {
  const badge = visitStatusBadge[status];
  const strip = visitStatusStrip[status];

  return (
    <td className={styles.statusCell}>
      {strip && <div className={`${styles.statusStrip} ${strip}`} />}
      <div className={styles.statusContent}>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </div>
    </td>
  );
}

function VisitTableRow({ visit }: { readonly visit: Visit }) {
  return (
    <tr className={styles.visitRow}>
      <StatusCell status={visit.status} />
      <td><span className={styles.siteName}>{visit.siteName}</span></td>
      <td>
        <span className={styles.cleanerCell}>
          <Avatar name={visit.cleanerName} size="sm" />
          <span className={styles.cleanerName}>{visit.cleanerName}</span>
        </span>
      </td>
      <td className={styles.hideMobile}>
        <span className={styles.shiftTime}>
          {formatTimeRange(visit.scheduledStart, visit.scheduledEnd)}
        </span>
      </td>
      <td className={styles.hideMobile}>
        <ActualCell visit={visit} />
      </td>
      <td>
        <DurationCell visit={visit} />
      </td>
      <td className={styles.hideMobile}>
        <GpsCell verification={visit.checkInVerification} />
      </td>
    </tr>
  );
}

function ActualCell({ visit }: { readonly visit: Visit }) {
  if (!visit.checkInTime) {
    return <span className={styles.notCheckedIn}>Not checked in</span>;
  }

  if (!visit.checkOutTime) {
    return (
      <span className={styles.noCheckout}>
        {formatCheckTime(visit.checkInTime)} – ?
      </span>
    );
  }

  return (
    <span className={styles.actualTime}>
      {formatCheckTime(visit.checkInTime)} – {formatCheckTime(visit.checkOutTime)}
    </span>
  );
}

function DurationCell({ visit }: { readonly visit: Visit }) {
  if (visit.durationMinutes !== null) {
    return <span className={styles.duration}>{formatDuration(visit.durationMinutes)}</span>;
  }
  return <span className={styles.noDuration}>–</span>;
}

function GpsCell({ verification }: { readonly verification: string | null }) {
  if (!verification || verification === "verified") return null;
  const label = gpsBadgeLabels[verification];
  if (!label) return null;
  return <span className={styles.gpsBadge}>{label}</span>;
}

function DateGroupHeader({
  date,
  visits,
  collapsed,
  onToggle,
}: {
  readonly date: string;
  readonly visits: Visit[];
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}) {
  const completedCount = visits.filter(
    (v) => v.status === "completed" || v.status === "late",
  ).length;

  return (
    <tr className={styles.dateGroupRow} onClick={onToggle}>
      <td colSpan={COL_COUNT}>
        <span className={styles.dateGroupContent}>
          <span className={styles.dateGroupLabel}>{formatDateHeader(date)}</span>
          <span className={styles.dateGroupMeta}>
            {visits.length} {visits.length === 1 ? "visit" : "visits"}, {completedCount} completed
          </span>
          <svg
            className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ""}`}
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.22 6.22a.75.75 0 011.06 0L8 8.94l2.72-2.72a.75.75 0 111.06 1.06l-3.25 3.25a.75.75 0 01-1.06 0L4.22 7.28a.75.75 0 010-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </td>
    </tr>
  );
}

function SummaryBar({ summary }: { readonly summary: VisitSummary }) {
  if (summary.totalVisits === 0) return null;

  return (
    <div className={styles.summary}>
      <span className={styles.stat}>
        <span className={styles.statValue}>{summary.totalVisits}</span>{" "}
        {summary.totalVisits === 1 ? "visit" : "visits"}
      </span>
      <span className={styles.stat}>
        <span className={styles.statValue}>{summary.completed}</span> completed
      </span>
      {summary.onTimePercent !== null && (
        <span className={styles.stat}>
          <span className={styles.statValue}>{summary.onTimePercent}%</span> on time
        </span>
      )}
      {summary.issues > 0 && (
        <span className={`${styles.stat} ${styles.statIssues}`}>
          <span className={styles.statValue}>{summary.issues}</span>{" "}
          {summary.issues === 1 ? "issue" : "issues"}
        </span>
      )}
      {summary.totalTrackedMinutes > 0 && (
        <span className={styles.stat}>
          <span className={styles.statValue}>{formatDuration(summary.totalTrackedMinutes)}</span> tracked
        </span>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className={styles.skeletonList} aria-busy="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className={styles.skeletonRow}>
          <Skeleton width="60px" height="20px" />
          <Skeleton width="100px" height="14px" />
          <Skeleton width="80px" height="14px" />
          <Skeleton width="90px" height="14px" />
        </div>
      ))}
    </div>
  );
}

// ── Page ──

export function VisitsPage() {
  usePageTitle("Visits | TidyRota");

  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<VisitsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(() => new Set());

  const defaultDate = yesterday();
  const from = searchParams.get("from") ?? defaultDate;
  const to = searchParams.get("to") ?? defaultDate;
  const siteId = searchParams.get("siteId") ?? "";
  const membershipId = searchParams.get("membershipId") ?? "";

  const presets = getPresets();
  const currentPreset = activePresetIndex(from, to, presets);

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (siteId) params.set("siteId", siteId);
      if (membershipId) params.set("membershipId", membershipId);

      const res = await fetch(`/api/visits?${params}`);
      if (!res.ok) return;
      const body = await res.json();
      if (body.success) {
        setData(body.data);
      }
    } finally {
      setLoading(false);
    }
  }, [from, to, siteId, membershipId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // When date range changes, reset collapsed state.
  // Collapse all dates except the most recent.
  useEffect(() => {
    if (!data || data.visits.length === 0) return;
    const dates = [...new Set(data.visits.map((v) => v.date))].sort().reverse();
    if (dates.length <= 1) {
      setCollapsedDates(new Set());
    } else {
      setCollapsedDates(new Set(dates.slice(1)));
    }
  }, [data]);

  const toggleDate = useCallback((date: string) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  }, []);

  function handlePreset(preset: DatePreset) {
    updateParams({ from: preset.from, to: preset.to });
  }

  function handleFromChange(e: React.ChangeEvent<HTMLInputElement>) {
    updateParams({ from: e.target.value });
  }

  function handleToChange(e: React.ChangeEvent<HTMLInputElement>) {
    updateParams({ to: e.target.value });
  }

  function handleSiteChange(e: React.ChangeEvent<HTMLSelectElement>) {
    updateParams({ siteId: e.target.value });
  }

  function handleCleanerChange(e: React.ChangeEvent<HTMLSelectElement>) {
    updateParams({ membershipId: e.target.value });
  }

  if (loading && !data) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <h1 className={styles.title}>Visits</h1>
          </div>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  const grouped = data ? groupByDate(data.visits) : new Map<string, Visit[]>();
  const sortedDates = [...grouped.keys()].sort().reverse();
  const isMultiDay = sortedDates.length > 1;

  const siteOptions: FilterOption[] = [
    { value: "", label: "All sites" },
    ...(data?.sites ?? []),
  ];

  const cleanerOptions: FilterOption[] = [
    { value: "", label: "All cleaners" },
    ...(data?.cleaners ?? []),
  ];

  return (
    <div className={styles.page}>
      {/* Header with date controls */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h1 className={styles.title}>Visits</h1>
          <div className={styles.dateControls}>
            <div className={styles.presetGroup}>
              {presets.map((preset, i) => (
                <button
                  key={preset.label}
                  type="button"
                  className={`${styles.preset} ${i === currentPreset ? styles.presetActive : ""}`}
                  onClick={() => handlePreset(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={styles.dateInputs}>
              <input
                type="date"
                value={from}
                onChange={handleFromChange}
                className={styles.dateInput}
                aria-label="From date"
              />
              <span className={styles.dateSep}>to</span>
              <input
                type="date"
                value={to}
                onChange={handleToChange}
                className={styles.dateInput}
                aria-label="To date"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <Select
          name="site"
          value={siteId}
          onChange={handleSiteChange}
          aria-label="Site"
          options={siteOptions}
          className={styles.filterSelect}
        />
        <Select
          name="cleaner"
          value={membershipId}
          onChange={handleCleanerChange}
          aria-label="Cleaner"
          options={cleanerOptions}
          className={styles.filterSelect}
        />
      </div>

      {/* Summary bar */}
      {data && <SummaryBar summary={data.summary} />}

      {/* Table or empty state */}
      {data && data.visits.length === 0 && (
        <div className={styles.emptyWrapper}>
          <EmptyState
            title="No visits found for this period"
            description="Try a different date range, or check the rota."
          />
        </div>
      )}

      {data && data.visits.length > 0 && (
        <div className={styles.tableCard}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <colgroup>
                <col style={{ width: "11%" }} />
                <col style={{ width: "19%" }} />
                <col style={{ width: "18%" }} />
                <col className={styles.hideMobile} style={{ width: "15%" }} />
                <col className={styles.hideMobile} style={{ width: "15%" }} />
                <col style={{ width: "9%" }} />
                <col className={styles.hideMobile} style={{ width: "13%" }} />
              </colgroup>
              <tbody>
                {sortedDates.map((date) => {
                  const dateVisits = grouped.get(date)!;
                  const collapsed = collapsedDates.has(date);

                  if (!isMultiDay) {
                    return (
                      <Fragment key={date}>
                        {dateVisits.map((visit) => (
                          <VisitTableRow key={visit.shiftId} visit={visit} />
                        ))}
                      </Fragment>
                    );
                  }

                  return (
                    <Fragment key={date}>
                      <DateGroupHeader
                        date={date}
                        visits={dateVisits}
                        collapsed={collapsed}
                        onToggle={() => toggleDate(date)}
                      />
                      {!collapsed &&
                        dateVisits.map((visit) => (
                          <VisitTableRow key={visit.shiftId} visit={visit} />
                        ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
